import React, { useState } from 'react';
import RewardCenterCardContainer from './RewardCenterCard';
import generateRewardCenterCardDataRendering from 'shared/screens/RewardCenter/utils/generateRewardCenterCardDataRendering';
import styled from 'styled-components';
import {
  formatOfferAmount,
  getOfferCardTitle,
  getOfferPriceValue,
  hasSoldOutState,
  isCoinOffer,
} from 'shared/screens/RewardCenter/utils/validations';
import { sendRewardCenterEvent } from 'shared/screens/RewardCenter/analytics/rewardCenterlogger';
import { REWARD_CENTER_EVENT_TYPES, REWARD_CENTER_OFFERS_STATUS } from 'shared/screens/RewardCenter/constants';
import { getCardTitleFontSize } from 'shared/screens/RewardCenter/utils/getCardTitleFontSize';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../../utils/mobileSupported';

const useMobileWebOfferLayout = isStandalonePWA || isNotStandaloneMobilePWA;
const mobileWebOfferTitleBase = window.innerHeight <= 575 ? 0.96 : 1.06;

const RewardCenterOffers = ({ object, rewardCenterAssets, RewardsImageBaseURL, setSelectedOffer }) => {
  const { Frame, Banner, Shadow, Loyalty, Coins, Badge } = generateRewardCenterCardDataRendering(
    object,
    rewardCenterAssets
  );
  const { offerId, offerImage, ribbontype, isNew } = object;
  const isCoinPricing = isCoinOffer(object);
  const hasSoldOutStateValue = hasSoldOutState(ribbontype);
  const priceText = formatOfferAmount(getOfferPriceValue(object));
  const offerTitle = getOfferCardTitle(object);
  const offerPricingIcon = isCoinPricing ? Coins : Loyalty;

  const [offerImageLoaded, setOfferImageLoaded] = useState(false);

  const handleClick = () => {
    if (hasSoldOutStateValue) {
      return;
    }

    setSelectedOffer(object);
    sendRewardCenterEvent(REWARD_CENTER_EVENT_TYPES.OPEN_REWARD_CENTER_OFFER, { offer: object, offerId });
  };

  return (
    <RewardCenterCardContainer handleClick={handleClick} Frame={Frame}>
      {Banner && <CardRibbon src={Banner} />}
      {Shadow && <CardShadow src={Shadow} />}
      <CardContent ribbontype={ribbontype}>
        {offerImage && RewardsImageBaseURL && (
          <CardImage src={`${RewardsImageBaseURL}${offerImage}`} onLoad={() => setOfferImageLoaded(true)} />
        )}
        {offerImageLoaded && (
          <DescriptionWrapper>
            <OfferContentStack>
              <OfferPrimarySlot>
                <OfferTitleWrapper>
                  <OfferTitle hasSoldOutState={hasSoldOutStateValue} titleLength={offerTitle.length}>
                    {offerTitle}
                  </OfferTitle>
                </OfferTitleWrapper>
              </OfferPrimarySlot>
              <OfferSecondarySlot>
                <OfferPriceWrapper>
                  <OfferPricingIcon src={offerPricingIcon} $isCoinPricing={isCoinPricing} />
                  <OfferPriceValue hasSoldOutState={hasSoldOutStateValue}>{priceText}</OfferPriceValue>
                </OfferPriceWrapper>
              </OfferSecondarySlot>
            </OfferContentStack>
          </DescriptionWrapper>
        )}
        {isNew && <NewOfferBadge src={Badge} />}
      </CardContent>
    </RewardCenterCardContainer>
  );
};

export default RewardCenterOffers;

const CardImage = styled.img`
  width: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
  border-top-left-radius: 10%;
  border-top-right-radius: 10%;
  height: auto;
  flex: 1;
  max-height: 84%;
`;

const CardShadow = styled.img`
  position: absolute;
  height: 100%;
  width: 100%;
  object-fit: contain;
  z-index: 3;
  opacity: 0.5;
  left: 0rem;
`;

const CardRibbon = styled.img`
  width: 75%;
  bottom: 35rem;
  z-index: 5;
  position: absolute;
  top: -2.3rem;
  left: 2.8rem;
  @media only screen and (max-height: 650px) {
    top: -1.2rem;
    left: 1.4rem;
  }
  @media only screen and (min-height: 651px) and (max-height: 800px) {
    top: -1.5rem;
    left: 2rem;
  }
  @media only screen and (min-height: 801px) and (max-height: 900px) {
    top: -2rem;
    left: 2.5rem;
  }
  @media only screen and (min-height: 901px) and (max-height: 949px) {
    top: -2.3rem;
    left: 3rem;
  }
  @media only screen and (min-height: 950px) {
    top: -2.4rem;
  }

  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.device('mobile-standalone') &&
    `
    left: 1.8vw !important;
  `}
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 90%;
  background-color: ${({ ribbontype }) =>
    ribbontype === REWARD_CENTER_OFFERS_STATUS.SOLD_OUT ? '#818181' : '#ac7601'};
  border-radius: 10%;
`;

const DescriptionWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 4.25%;
  width: 82%;
  height: ${useMobileWebOfferLayout ? '22%' : '17%'};
  min-height: ${useMobileWebOfferLayout ? '3.35rem' : '0'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  z-index: 10;
`;

const OfferContentStack = styled.div`
  height: 100%;
  width: 100%;
  display: ${useMobileWebOfferLayout ? 'grid' : 'flex'};
  ${useMobileWebOfferLayout
    ? `
      grid-template-rows: minmax(0, 1fr) auto;
    `
    : `
      flex-direction: column;
      justify-content: space-between;
    `}
  align-items: center;
  gap: ${useMobileWebOfferLayout ? '0.04rem' : '0'};
  padding: ${useMobileWebOfferLayout ? '0.18rem 0 0.16rem' : '4% 0 6%'};
  box-sizing: border-box;
  overflow: hidden;
`;

const OfferPrimarySlot = styled.div`
  width: 100%;
  flex-direction: column;
  display: flex;
  align-items: center;
  height: ${useMobileWebOfferLayout ? '100%' : '56%'};
  justify-content: ${useMobileWebOfferLayout ? 'center' : 'flex-start'};
  min-height: 0;
  overflow: hidden;
`;

const OfferSecondarySlot = styled.div`
  width: 100%;
  min-height: ${useMobileWebOfferLayout ? '1.05rem' : '19%'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${useMobileWebOfferLayout ? 'center' : 'flex-end'};
  overflow: hidden;
`;

const OfferTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${useMobileWebOfferLayout ? '100%' : 'auto'};
  min-height: 0;
  overflow: hidden;
`;

const OfferTitle = styled.p`
  font-size: ${({ titleLength }) =>
    getCardTitleFontSize(titleLength, useMobileWebOfferLayout ? mobileWebOfferTitleBase : 1)};
  text-transform: uppercase;
  text-align: center;
  color: ${({ hasSoldOutState }) => (hasSoldOutState ? '#BCBBBA' : '#fff')};
  font-weight: 700;
  margin: 0;
  padding: 0 0.25rem;
  word-break: break-word;
  line-height: ${({ titleLength }) => (titleLength > 26 ? 1.02 : 1.08)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${({ titleLength }) =>
    !useMobileWebOfferLayout &&
    `
    @media only screen and (min-height: 660px) {
      font-size: ${getCardTitleFontSize(titleLength, 1.45)};
    }
    @media only screen and (min-height: 900px) {
      font-size: ${getCardTitleFontSize(titleLength, 1.6)};
    }
    @media only screen and (min-height: 1000px) {
      font-size: ${getCardTitleFontSize(titleLength, 1.9)};
    }
    /* Narrow desktop windows: shrink so 2-line clamp doesn't truncate. */
    @media only screen and (max-width: 1500px) {
      font-size: ${getCardTitleFontSize(titleLength, 1.1)};
    }
    @media only screen and (max-width: 1300px) {
      font-size: ${getCardTitleFontSize(titleLength, 0.95)};
    }
    @media only screen and (max-width: 1100px) {
      font-size: ${getCardTitleFontSize(titleLength, 0.85)};
    }
    @media only screen and (max-width: 900px) {
      font-size: ${getCardTitleFontSize(titleLength, 0.75)};
    }
    @media only screen and (max-width: 760px) {
      font-size: ${getCardTitleFontSize(titleLength, 0.65)};
    }
  `}
`;

const OfferPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 0 0 auto;
  min-width: 0;
  overflow: hidden;
`;

const OfferPricingIcon = styled.img`
  height: ${({ $isCoinPricing }) =>
    useMobileWebOfferLayout ? ($isCoinPricing ? '0.95rem' : '1.15rem') : $isCoinPricing ? '1.1rem' : '1.8rem'};
  width: ${({ $isCoinPricing }) =>
    useMobileWebOfferLayout ? ($isCoinPricing ? '0.95rem' : '1.15rem') : $isCoinPricing ? '1.1rem' : '1.8rem'};
  margin-right: 0.25rem;
  flex-shrink: 0;
  object-fit: contain;
`;

const OfferPriceValue = styled.p`
  font-size: ${useMobileWebOfferLayout ? '0.92rem' : '1.1rem'};
  line-height: 1;
  color: ${({ hasSoldOutState }) => (hasSoldOutState ? '#BCBBBA' : '#fff')};
  font-weight: 600;
  text-align: center;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${!useMobileWebOfferLayout &&
  `
    @media only screen and (max-height: 659px) {
      font-size: ${window.innerHeight < 320 ? 0.7 : 0.8}rem;
    }
    @media only screen and (min-height: 660px) and (max-height: 900px) {
      font-size: 1rem;
    }
    @media only screen and (min-height: 950px) {
      font-size: 1.2rem;
    }
    /* Narrow desktop windows: shrink so the price + icon row fits. */
    @media only screen and (max-width: 1500px) {
      font-size: 0.95rem;
    }
    @media only screen and (max-width: 1300px) {
      font-size: 0.88rem;
    }
    @media only screen and (max-width: 1100px) {
      font-size: 0.82rem;
    }
    @media only screen and (max-width: 900px) {
      font-size: 0.75rem;
    }
    @media only screen and (max-width: 760px) {
      font-size: 0.68rem;
    }
  `}
`;

const NewOfferBadge = styled.img`
  position: absolute;
  width: 80px;
  height: 80px;
  z-index: 10;
  left: 21rem;
  bottom: 36.2rem;
  filter: drop-shadow(-2px 4px 8px #000000);

  @media only screen and (min-height: 660px) and (max-height: 800px) {
    width: 65px;
    height: 65px;
    left: 12.5rem;
    bottom: 22.2rem;
  }
  @media only screen and (min-height: 801px) and (max-height: 900px) {
    left: 17rem;
    bottom: 29.2rem;
  }
`;
